import React from 'react';
import { Container, Navbar as Nav } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import config from '../../../../config/channels';
import { TPage } from '../AppLayout';
import { TSite } from '../../../../contexts/site/site-context';
import useSiteContext from '../../../../contexts/site/useSiteContext';
import useOrderContext from '../../../../contexts/order/useOrderContext';
import useScrollPosition from "../../../../hooks/useScrollPosition";

interface NavbarProps {
    page: TPage;
    dineInSite?: TSite;
}

const Navbar = ({ page, dineInSite }: NavbarProps) => {
    const siteContext = useSiteContext();
    const orderContext = useOrderContext();
    const scrollPosition = useScrollPosition();
    const navigate = useNavigate();

    const channelHandle = config[siteContext.channel]['handle'];
    const siteHandle = orderContext.order?.site?.handle;
    const backURL = channelHandle && siteHandle ? `/${channelHandle}/${siteHandle}` : "/";

    if (page === 'menu') return null;

    return (
        <StyledNav className={`${scrollPosition > 64 ? 'scrolled' : ''} nav-${page}`} dineIn={page === 'order-at-table'}>
            {page === 'order-at-table' ? (
                <div className="nav-top__logo">
                    <NavContent>
                        <StyledNavBrand onClick={() => {
                            navigate(`/${config.dineIn.handle}`);
                        }} className="logoClass" />
                        <StyledRightPanel>
                            <div className={'overflow-location'}>
                                <StyledLocation
                                    role="button"
                                    style={{ fontSize: '14px' }}
                                    onClick={() => { navigate(`/${config.dineIn.handle}`); }}
                                >
                                    <span className="material-symbols-outlined">location_on</span>
                                    <span className="location">{dineInSite?.name ?? siteContext.site?.name}</span>
                                </StyledLocation>
                            </div>
                        </StyledRightPanel>
                    </NavContent>
                </div>
            ) : (
                <StyledContainer>
                    {page !== 'checkout' && page !== 'confirmation' && (
                        <StyledNavBrand onClick={() => {
                            navigate('/');
                        }} className="logoClass" />
                    )}

                    {page === 'checkout' && (
                        <>
                            <div className="nav-checkout__btn">
                                <button onClick={() => {
                                    navigate(-1);
                                }}>
                                    <span className="material-symbols-outlined">chevron_backward</span>
                                </button>
                            </div>
                            <StyledNavBrand center className="logoClass" />
                        </>
                    )}

                    {page === 'confirmation' && (
                        <>
                            <StyledNavBrand center onClick={() => {
                                navigate(backURL);
                            }} className="logoClass" />
                        </>
                    )}
                </StyledContainer>
            )}
        </StyledNav>
    );
};

const StyledContainer = styled(Container)`
    max-width: 100%;
    margin: 0 auto;
    padding: 0;

    .nav-checkout__btn {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        margin-left: 20px;

        button {
            border: none;
            margin: 0 12px 0 0;
            padding: 0;
            width: 40px;
            height: 40px;
            position: relative;
            z-index: 2;
            background: var(--layouts_header_bg);
            color: var(--color_ui_4);
            display: flex;
            align-items: center;
            justify-content: left;
            border-radius: 50%;
            font-size: 24px;
            line-height: 24px;

            &:hover { text-decoration: none; }

            transition: background-color 0.15s ease-in-out;
        }
    }
`;

const StyledNavBrand = styled.div<{center?: boolean}>`
    background-image: var(--logo);
    background-size: contain;
    background-repeat: no-repeat;
    height: var(--logo_height);
    ${props => props.center ? `
        margin: 0 auto;
        background-position: center center;
    ` : `
        margin: 0;
        background-position: center left;
    `};
    width: 100%;
    display: block;
    cursor: pointer;
    flex-shrink: 0;

    @media screen and (min-width: 768px) {
        width: 290px;
    }
`;

const StyledNav = styled(Nav)<{dineIn: boolean}>`
    position: relative;
    z-index: 999;
    background-color: var(--layouts_header_bg);
    display: flex;
    align-items: center;
    margin: 0;
    padding: 8px 16px;
    width: 100%;
    position: fixed;
    left: 0;
    top: 0;
    right: 0;
    border-bottom: var(--layouts_nav_border);
    box-shadow: 0 1px 4px rgb(0 0 0 / 8%);

    ${props => props.dineIn ? `
        @media screen and (min-width: 768px) {
            height: 60px;
            align-items: center;
            flex-direction: row;
        }

        .nav-top__logo {
            max-width: 1296px;
            padding: 0 48px;
            margin: 0 auto;
            width: 100%;
            @media screen and (max-width: 767px) { padding: 8px 0; }
        }
    ` : `
        height: 60px;
    `};

    position: fixed;
    top: 0;
    right: 0;
    left: 0;

    @media screen and (min-width: 993px) {
        padding: 8px 60px;
    }

    &.nav-checkout,
    &.nav-confirmation
    {
        .container {
            max-width: 1092px;
            margin: 0 auto;
        }

    }
    &.nav-channels {
        @media screen and (min-width: 768px) {
            padding: 8px 60px;
        }
    }
    &.nav-order-at-table {
        .logoClass:hover {
            cursor: default !important;
        }
    }

    &.nav-checkout {
        @media screen and (min-width: 768px) {
            padding: 8px 20px;
        }
        @media screen and (min-width: 993px) {
            padding: 8px 60px;
        }
    }
`;

const NavContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    flex-wrap: wrap;
`;

const StyledRightPanel = styled.div`
    display: flex;
    gap: 24px;
    min-width: 0;
    align-items: center;
    flex-shrink: 1;
    .overflow-location {
        overflow: hidden;
    }
`;

const StyledLocation = styled.div`
    display: flex;
    align-items: center;
    height: 36px;
    padding-top: 16px;
    overflow: hidden;

    @media screen and (min-width: 768px) {
        padding: 0 0 0 16px;
    }

    span {
        display: inline-block;
        color: var(--color_ui_4);
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    .material-symbols-outlined {
        margin-right: 4px;
        font-size: 24px;
        line-height: 24px;
    }

    .location {
        text-decoration: underline;
        text-underline-offset: 2px;
    }
`;

export default Navbar;
